import { Fragment, type KeyboardEvent, type MouseEvent } from "react"
import type { ChecklistItem as ChecklistItemDto } from "services/api/checklistClient"
import { Check, Download, LinkIcon, ListOrdered } from "lucide-react"
import { cn } from "lib/utils"

export type ChecklistItem = ChecklistItemDto & { subtitle?: string }

type ChecklistCardProps = {
  items: ChecklistItem[]
  loading: boolean
  error: string | null
  showCompleted: boolean
  onToggleShowCompleted: (checked: boolean) => void
  onToggleItem: (itemId: string) => void
  onLinkClick: (event: MouseEvent, item: ChecklistItem) => void | Promise<void>
  getSubtitle: (item: ChecklistItem) => string
  title?: string
  previewLimit?: number
  onOpenAll?: () => void
  className?: string
  savingItemIds?: ReadonlySet<string>
  rowErrors?: Readonly<Record<string, string>>
  onRetryItem?: (itemId: string) => void
  recentlyCompletedItemId?: string | null
}

export const ChecklistCard = ({
  items,
  loading,
  error,
  showCompleted,
  onToggleShowCompleted,
  onToggleItem,
  onLinkClick,
  getSubtitle,
  title = "This week",
  previewLimit,
  onOpenAll,
  className,
  savingItemIds = new Set<string>(),
  rowErrors = {},
  onRetryItem,
  recentlyCompletedItemId = null,
}: ChecklistCardProps) => {
  const allFilteredItems = showCompleted
    ? [...items.filter((item) => !item.completedAt), ...items.filter((item) => item.completedAt)]
    : items.filter((item) => !item.completedAt || item.id === recentlyCompletedItemId)
  const filteredItems = typeof previewLimit === "number" ? allFilteredItems.slice(0, previewLimit) : allFilteredItems
  const incompleteCount = items.filter((item) => !item.completedAt).length

  return (
    <section className={cn("cohort-open-section cohort-member-band cohort-member-band--white min-w-0", className)} aria-labelledby="cohort-checklist-heading">
      <p className="cohort-section-eyebrow lg:hidden">This week</p>
      <div className="cohort-card-heading">
        <h2 id="cohort-checklist-heading" className="text-[19px] text-[var(--cohort-ink)] lg:text-[22px]"><span className="lg:hidden">Your checklist</span><span className="hidden lg:inline">{title}</span></h2>
        <div className="flex items-center gap-3 text-sm text-[var(--cohort-ink-muted)]">
          {items.length ? <span>{incompleteCount} of {items.length} left</span> : null}
          {typeof previewLimit !== "number" ? (
            <button type="button" onClick={() => onToggleShowCompleted(!showCompleted)} className="cohort-text-action min-h-11">
              {showCompleted ? "Hide finished" : "Show finished"}
            </button>
          ) : null}
        </div>
      </div>
      <div>
        {loading && <div className="py-5 text-sm text-[var(--cohort-ink-muted)]">Loading checklist…</div>}
        {error && !loading && <div className="my-3 rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] p-3 text-sm text-[var(--cohort-clay)]">{error}</div>}
        {!loading && !error && filteredItems.length === 0 ? (
          <div className="py-5 text-sm leading-6 text-[var(--cohort-ink-muted)]">
            {items.length ? (
              <><p className="font-medium text-[var(--cohort-ink)]">You&apos;re caught up.</p><p>Nothing needs doing before the next session.</p></>
            ) : (
              <><p className="font-medium text-[var(--cohort-ink)]">Nothing assigned yet.</p><p>Tasks your mentor sets will show up here.</p></>
            )}
          </div>
        ) : null}
        {!loading && !error && filteredItems.length > 0 && (
          <div className="cohort-bounded-list cohort-checklist-list w-full" role="region" aria-label={`${title} list`}>
            {filteredItems.map((item, index) => {
              const isCompleted = Boolean(item.completedAt)
              const isRecentlyCompleted = isCompleted && item.id === recentlyCompletedItemId
              const subtitle = (isRecentlyCompleted ? "Ticked off just now" : item.subtitle ?? getSubtitle(item)).trim()
              const description = item.description?.trim()
              const mediaType = (item.media?.mediaType || "").toLowerCase()
              const hasExternalLink = Boolean(item.externalUrl)
              const hasMediaLink = Boolean(item.media)
              const flowName = item.questionFlowName?.trim()
              const hasFlowLink = Boolean(flowName)
              const canToggle = !hasFlowLink && !item.questionFlow
              const isDownload = hasMediaLink && mediaType !== "video" && !hasExternalLink
              const isSaving = savingItemIds.has(item.id)
              const rowError = rowErrors[item.id]
              const showFinishedHeader = showCompleted && typeof previewLimit !== "number" && isCompleted && !filteredItems[index - 1]?.completedAt
              const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
                if (event.target !== event.currentTarget || !canToggle || isSaving) return
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  onToggleItem(item.id)
                }
              }

              return (
                <Fragment key={item.id}>
                {showFinishedHeader ? <p className="cohort-checklist-group-label py-3 text-xs font-medium uppercase tracking-[0.08em] text-[var(--cohort-ink-subtle)]">Finished</p> : null}
                <div
                  data-testid={`cohort-checklist-row-${item.id}`}
                  onClick={() => {
                    if (!canToggle || isSaving) return
                    onToggleItem(item.id)
                  }}
                  onKeyDown={handleKeyDown}
                  role={canToggle ? "button" : undefined}
                  tabIndex={canToggle ? 0 : -1}
                  className={cn(
                    "cohort-checklist-row cohort-ruled-row cohort-row-action flex w-full min-w-0 items-start gap-3 py-[15px] text-left sm:gap-4 sm:px-5 sm:py-[18px]",
                    canToggle && !isSaving ? "cursor-pointer" : "cursor-default",
                  )}
                >
                  <span data-testid={`cohort-checklist-toggle-${item.id}`} className={cn("mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border-[1.5px]", isCompleted ? "border-[var(--cohort-ink)] bg-[var(--cohort-ink)] text-white" : hasFlowLink ? "border-dashed border-[var(--cohort-radio-idle)]" : "border-[var(--cohort-radio-idle)]")}>{isCompleted ? <Check className="h-3.5 w-3.5" /> : null}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1">
                      {hasExternalLink || hasMediaLink || hasFlowLink ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            void onLinkClick(event, item)
                          }}
                          className={`cohort-text-action break-words text-left text-base font-medium sm:text-[17px] ${
                            isCompleted ? "line-through decoration-[var(--cohort-radio-idle)] text-[var(--cohort-ink-muted)]" : "text-[var(--cohort-ink)] hover:underline"
                          }`}
                        >
                          {item.title}
                        </button>
                      ) : (
                        <p className={`break-words text-base font-medium sm:text-[17px] ${isCompleted ? "line-through decoration-[var(--cohort-radio-idle)] text-[var(--cohort-ink-muted)]" : "text-[var(--cohort-ink)]"}`}>
                          {item.title}
                        </p>
                      )}
                      {hasExternalLink && <LinkIcon className="h-4 w-4 shrink-0 text-[var(--cohort-ink-muted)]" />}
                      {hasFlowLink && <ListOrdered className="h-4 w-4 shrink-0 text-[var(--cohort-ink-muted)]" />}
                      {isDownload && <Download className="h-4 w-4 shrink-0 text-[var(--cohort-ink-muted)]" />}
                    </div>
                    {description && (
                      <p className="mt-1 break-words text-sm leading-[1.5] text-[var(--cohort-ink-muted)] sm:text-[15px]">
                        {description}
                      </p>
                    )}
                    {subtitle || canToggle ? (
                      <p data-testid={`cohort-checklist-meta-${item.id}`} className={cn("mt-1 min-h-5 text-sm leading-5", !isCompleted && subtitle.toLowerCase().includes("due") ? "text-[var(--cohort-ochre)]" : "text-[var(--cohort-ink-muted)]")}>
                        {subtitle}
                        {isSaving ? <span className="text-[var(--cohort-ink-muted)]" role="status">{subtitle ? " · " : ""}Saving…</span> : null}
                      </p>
                    ) : null}
                    {flowName && !isCompleted && (
                      <p className="mt-1 break-words text-sm text-[var(--cohort-ink-muted)]">
                        Complete "{flowName}" to finish this task.
                      </p>
                    )}
                    {rowError ? (
                      <div className="mt-2 text-sm text-[var(--cohort-clay)]" role="alert">
                        <p>{rowError}</p>
                        {onRetryItem ? <button type="button" onClick={(event) => { event.stopPropagation(); onRetryItem(item.id) }} className="cohort-text-action min-h-11 font-medium text-[var(--cohort-clay)]">Try again</button> : null}
                      </div>
                    ) : null}
                  </div>
                  {hasFlowLink && !isCompleted ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        void onLinkClick(event, item)
                      }}
                      className="cohort-primary-button hidden min-h-[46px] shrink-0 items-center px-6 text-base sm:inline-flex"
                    >
                      Open the module
                    </button>
                  ) : null}
                </div>
                </Fragment>
              )
            })}
          </div>
        )}
        {onOpenAll && (allFilteredItems.length > filteredItems.length || typeof previewLimit === "number") ? (
          <button type="button" onClick={onOpenAll} className="cohort-text-action mt-2 min-h-11 text-[15px]">
            Open the checklist
          </button>
        ) : null}
      </div>
    </section>
  )
}
